/**
 * "The server has revoked this session" — the one place the app learns of it.
 *
 * Module scope for the reason `maintenanceStore.ts` gives: the detector runs inside
 * `openapi-fetch`, which has no React tree above it. `AuthProvider` subscribes and signs out.
 *
 * **Single-flight lives here.** A page fires many requests at once, so a revocation
 * arrives as a burst of identical 401s. `report` ignores every call while a revocation
 * is already held, so exactly one subscriber notification — and so one sign-out — comes
 * out of any burst. The hold is released only by `clear`, which `AuthProvider` calls when
 * a new session is established; until then the held message is also what the sign-in
 * page shows.
 */

type Listener = () => void

let message: string | null = null
const listeners = new Set<Listener>()

/** The server's message while a revocation is held, otherwise `null`. */
export function getSessionRevoked(): string | null {
  return message
}

/** Record a revocation. A no-op while one is already held (single-flight). */
export function reportSessionRevoked(next: string): void {
  if (message !== null) return
  message = next
  listeners.forEach((listener) => listener())
}

/** Release the hold — a new session now exists. */
export function clearSessionRevoked(): void {
  if (message === null) return
  message = null
  listeners.forEach((listener) => listener())
}

export function subscribeSessionRevoked(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Test seam only. */
export function __resetSessionRevokedForTests(): void {
  message = null
  listeners.clear()
}
